import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from "react-router-dom";
import { fetchETFs } from '../../api/tickersApi';
import { flattenEtfPositions, calculateOverlap } from "../../lib/etfTools";
import { addEtfToMatrix, removeEtfFromMatrix, createNewEtfMatrix } from "../../store/slices/appState.slice";

const useEtfMatrix = () => {
    const selectedTickers = useSelector(state => state.appStates.etfMatrix.etfList); // Use Redux state for selected tickers
    const [showSearch, setShowSearch] = useState(false);
    const [filters, setFilters] = useState({ search: [], sector: [] });
    const [positionCollectionMode, setPositionCollectionMode] = useState('matrix'); // ['matrix', 'consolidated']
    const dispatch = useDispatch();

    const { positions, status } = useSelector(state => state.tickers.etfs);
    const etfColors = useSelector(state => state.appStates.etfColors);

    const flattenedPositions = useMemo(() => positions ? flattenEtfPositions(Object.values(positions)) : [], [positions]);
    const overlapData = useMemo(() => flattenedPositions ? calculateOverlap(flattenedPositions) : null, [flattenedPositions]);

    useEffect(() => {
        if (selectedTickers.length > 0) {
            dispatch(fetchETFs(selectedTickers));
        }
    }, [dispatch, selectedTickers]);

    const handleTickersSubmit = (newTickers) => {
        const uniqueNewTickers = newTickers.filter(newTicker => !selectedTickers.some(ticker => ticker.symbol === newTicker.symbol));
        if (uniqueNewTickers) {
            uniqueNewTickers.forEach(ticker => dispatch(addEtfToMatrix(ticker.symbol)));
        }
        setShowSearch(false);
    };

    const handleRemoveTicker = (ticker) => {
        dispatch(removeEtfFromMatrix(ticker.symbol));
    };

    const handleReset = () => {
        dispatch(createNewEtfMatrix([]));
    };

    const handleAddETFsClick = () => {
        setShowSearch(true);
    };

    const handleSelectGroup = (etfs) => {
        const etfObjects = etfs.map(etf => ({ symbol: etf.toLowerCase() }));
        handleTickersSubmit(etfObjects);
    };

    const addTickerToSearch = (symbol) => {
        const searchFilters = [...filters.search, symbol];
        setFilters(prevFilters => ({
            ...prevFilters,
            search: searchFilters
        }));
    };

    const removeTickerFromSearch = (symbol) => {
        const searchFilters = filters.search.filter(s => s !== symbol);
        setFilters(prevFilters => ({
            ...prevFilters,
            search: searchFilters
        }));
    };

    const clearTickersFromSearch = () => {
        setFilters(prevFilters => ({
            ...prevFilters,
            search: []
        }));
    };

    return {
        selectedTickers,
        showSearch,
        filters,
        positionCollectionMode,
        etfColors,
        overlapData,
        flattenedPositions,
        positions,
        handleTickersSubmit,
        handleRemoveTicker,
        handleReset,
        handleAddETFsClick,
        handleSelectGroup,
        addTickerToSearch,
        removeTickerFromSearch,
        clearTickersFromSearch,
        setShowSearch,
    };
};

export default useEtfMatrix;
